import { Trans } from '@lingui/react'
import IBAN from 'iban'
import sanitizeHtml from 'sanitize-html'

import {
  accountNumberRegExp,
  addressFormMaxLength,
  bankAccountRegExp,
  creditCardCvcRegExp,
  creditCardNumberRegExp,
  dayAndMonthRegExp,
  emailRegExp,
  generalPhoneRegExp,
  onlyCharactersRegExp,
  passwordRegExp,
  phoneRegExp,
  salutationRegExp,
  simpleTextFieldOnlyCharactersRegExp,
  simpleTextFieldRegExp,
  streetAccountRegExp,
  streetRegExp,
  yearRegExp,
} from 'views/consts/validation'

import { isTaxNumberValid } from './taxNumberValidator'

const simpleTextFieldValidator = {
  pattern: {
    value: simpleTextFieldRegExp.source,
    message: <Trans id="form.error.input.invalid" />,
  },
  required: {
    message: <Trans id="form.error.input.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: 25,
    message: (
      <Trans id="form.error.input.maxLength" values={{ maxLength: 25 }} />
    ),
  },
}

const firstNameFieldValidator = {
  pattern: {
    value: onlyCharactersRegExp,
    message: <Trans id="form.error.input.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.firstName.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: addressFormMaxLength,
    message: (
      <Trans
        id="form.error.input.maxLength"
        values={{ maxLength: addressFormMaxLength }}
      />
    ),
  },
}

const lastNameFieldValidator = {
  pattern: {
    value: onlyCharactersRegExp,
    message: <Trans id="form.error.input.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.lastName.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: addressFormMaxLength,
    message: (
      <Trans
        id="form.error.input.maxLength"
        values={{ maxLength: addressFormMaxLength }}
      />
    ),
  },
}

const cityValidator = {
  pattern: {
    value: simpleTextFieldOnlyCharactersRegExp,
    message: <Trans id="form.error.input.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.city.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
}

const zipValidator = zipCodeValidationRules => {
  return {
    pattern: {
      value: new RegExp(zipCodeValidationRules.regex),
      message: <Trans id="address.error.zip.invalid" />,
    },
    required: {
      value: true,
      message: <Trans id="address.error.zip.missing" />,
    },
    maxLength: {
      value: zipCodeValidationRules.length,
      message: (
        <Trans
          id="form.error.input.maxLength"
          values={{ maxLength: zipCodeValidationRules.length }}
        />
      ),
    },
  }
}

const phoneValidator = {
  pattern: {
    value: phoneRegExp,
    message: <Trans id="address.error.phone.invalid" />,
  },
  required: {
    value: false,
    message: <Trans id="address.error.phone.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: 25,
    message: (
      <Trans id="form.error.input.maxLength" values={{ maxLength: 25 }} />
    ),
  },
}

const generalPhoneValidator = {
  validate: value => {
    const phone = value.split(' ').join('')
    if (phone.length === 0) {
      return
    }
    if (phone.replace('+', '').length < 8) {
      return <Trans id="form.error.input.minLength" values={{ minLength: 8 }} />
    }
    if (!generalPhoneRegExp.test(phone)) {
      return <Trans id="address.error.phone.invalid" />
    }
  },
  maxLength: {
    value: 25,
    message: (
      <Trans id="form.error.input.maxLength" values={{ maxLength: 25 }} />
    ),
  },
}

const accountNumberValidator = {
  pattern: {
    value: accountNumberRegExp.source,
    message: <Trans id="address.error.account.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.account.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: 25,
    message: (
      <Trans id="form.error.input.maxLength" values={{ maxLength: 25 }} />
    ),
  },
}

const bankAccountValidator = {
  pattern: {
    value: bankAccountRegExp.source,
    message: <Trans id="address.error.bankAccount.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.bankAccount.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: 25,
    message: (
      <Trans id="form.error.input.maxLength" values={{ maxLength: 25 }} />
    ),
  },
}

const creditCardCvcValidator = {
  pattern: {
    value: creditCardCvcRegExp.source,
    message: <Trans id="address.error.cvc.invalid" />,
  },
}

const creditCardNumberValidator = {
  pattern: {
    value: creditCardNumberRegExp.source,
    message: <Trans id="address.error.creditCardNumber.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.creditCardNumber.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: 25,
    message: (
      <Trans id="form.error.input.maxLength" values={{ maxLength: 25 }} />
    ),
  },
}

const dayAndMonthValidator = {
  pattern: {
    value: dayAndMonthRegExp.source,
    message: <Trans id="address.error.dayAndMonth.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.dayAndMonth.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: 25,
    message: (
      <Trans id="form.error.input.maxLength" values={{ maxLength: 25 }} />
    ),
  },
}

const emailValidator = {
  pattern: {
    value: emailRegExp.source,
    message: <Trans id="address.error.email.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.email.missing" />,
  },
  minLength: {
    value: 5,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 5 }} />
    ),
  },
  maxLength: {
    value: addressFormMaxLength,
    message: (
      <Trans
        id="form.error.input.maxLength"
        values={{ maxLength: addressFormMaxLength }}
      />
    ),
  },
}

const taxNumberValidator = taxNumberValidationRules => {
  const validation = {
    required: {
      value: false,
      message: '',
    },
    pattern: {
      value: new RegExp(taxNumberValidationRules.regex),
      message: <Trans id="address.error.taxNumber.invalid" />,
    },
    maxLength: {
      value: taxNumberValidationRules.length,
      message: (
        <Trans
          id="form.error.input.maxLength"
          values={{ maxLength: taxNumberValidationRules.length }}
        />
      ),
    },
    validate: value => {
      if (value === '') {
        return
      }
      if (taxNumberValidationRules.tenant === 'BE') {
        return
      }
      if (!isTaxNumberValid(value)) {
        return <Trans id="address.error.taxNumber.invalid" />
      }
    },
  }
  return validation
}

const passwordValidator = {
  pattern: {
    value: passwordRegExp.source,
    message: <Trans id="address.error.password.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.password.missing" />,
  },
  minLength: {
    value: 5,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 5 }} />
    ),
  },
  maxLength: {
    value: addressFormMaxLength,
    message: (
      <Trans
        id="form.error.input.maxLength"
        values={{ maxLength: addressFormMaxLength }}
      />
    ),
  },
}

const salutationValidator = {
  pattern: {
    value: salutationRegExp,
    message: <Trans id="address.error.salutation.invalid" />,
  },
  required: {
    value: true,
    message: <Trans id="address.error.salutation.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: addressFormMaxLength,
    message: (
      <Trans
        id="form.error.input.maxLength"
        values={{ maxLength: addressFormMaxLength }}
      />
    ),
  },
}

const streetNumberValidator = streetNumberValidationRules => {
  const validation = {
    pattern: {
      value: new RegExp(streetNumberValidationRules.regex),
      message: <Trans id="address.error.streetNumber.invalid" />,
    },
    required: {
      value: true,
      message: <Trans id="address.error.streetNumber.missing" />,
    },
    maxLength: {
      value: streetNumberValidationRules.length,
      message: (
        <Trans
          id="form.error.input.maxLength"
          values={{ maxLength: streetNumberValidationRules.length }}
        />
      ),
    },
  }
  return validation
}

const letterBoxValidator = letterBoxValidationRules => {
  const validation = {
    pattern: {
      value: new RegExp(letterBoxValidationRules.regex),
      message: <Trans id="address.error.letterBoxNumber.invalid" />,
    },
    required: {
      value: false,
      message: '',
    },
    maxLength: {
      value: letterBoxValidationRules.length,
      message: (
        <Trans
          id="form.error.input.maxLength"
          values={{ maxLength: letterBoxValidationRules.length }}
        />
      ),
    },
  }
  return validation
}

const streetValidator = streetValidationRules => {
  const validation = {
    pattern: {
      value: streetRegExp,
      message: <Trans id="address.error.street.invalid" />,
    },
    required: {
      value: true,
      message: <Trans id="address.error.street.missing" />,
    },
    minLength: {
      value: 2,
      message: (
        <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
      ),
    },
    maxLength: {
      value: streetValidationRules.length,
      message: (
        <Trans
          id="form.error.input.maxLength"
          values={{ maxLength: streetValidationRules.length }}
        />
      ),
    },
  }
  return validation
}

const extensionValidator = extensionMaxLength => {
  const validation = {
    validate: value => {
      const sanitizedValue = sanitizeHtml(value, { allowedTags: [] })
      if (sanitizedValue !== value) {
        return <Trans id="form.error.input.invalid" />
      }
      return true
    },
    maxLength: {
      value: extensionMaxLength,
      message: (
        <Trans
          id="form.error.input.maxLength"
          values={{ maxLength: extensionMaxLength }}
        />
      ),
    },
  }
  return validation
}

const streetOnlyCharactersValidator = streetValidationRules => {
  const validation = {
    ...streetValidator(streetValidationRules),
    pattern: {
      value: streetAccountRegExp,
      message: <Trans id="address.error.street.invalid" />,
    },
  }
  return validation
}

const yearValidator = {
  pattern: {
    value: yearRegExp.source,
    message: <Trans id="address.error.year.invalid" />,
  },
  required: {
    message: <Trans id="address.error.year.missing" />,
  },
  minLength: {
    value: 2,
    message: (
      <Trans id="form.error.input.minLength" values={{ minLength: 2 }} />
    ),
  },
  maxLength: {
    value: addressFormMaxLength,
    message: (
      <Trans
        id="form.error.input.maxLength"
        values={{ maxLength: addressFormMaxLength }}
      />
    ),
  },
}

const ibanValidator = iban =>
  IBAN.isValid(iban) || (
    <Trans id="paymentForm.directDebit.error.iban.invalid" />
  )

export {
  firstNameFieldValidator,
  lastNameFieldValidator,
  phoneValidator,
  generalPhoneValidator,
  zipValidator,
  cityValidator,
  accountNumberValidator,
  bankAccountValidator,
  creditCardCvcValidator,
  creditCardNumberValidator,
  dayAndMonthValidator,
  emailValidator,
  taxNumberValidator,
  passwordValidator,
  salutationValidator,
  simpleTextFieldValidator,
  streetNumberValidator,
  streetValidator,
  streetOnlyCharactersValidator,
  yearValidator,
  ibanValidator,
  extensionValidator,
  letterBoxValidator,
}
